import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, CircularProgress, Box, Typography } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import ApiNotices from '@components/Organization/ApiNotices';
import { makeTheme } from '@theme/index';
import { PreferencesProvider, usePreferences } from '@context/PreferencesContext';
import { AuthProvider, useAuth } from '@context/AuthContext';
import { NotificationProvider } from '@context/NotificationContext';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { UserRole } from './types';

// Auth pages (small, eager — needed on first paint for unauthenticated users)
import LoginPage from '@pages/LoginPage';
import RegisterPage from '@pages/RegisterPage';
import ForgotPasswordPage from '@pages/ForgotPasswordPage';
import DashboardLayout from '@pages/DashboardLayout';

// Lazy-loaded feature pages
const PatientDashboard = lazy(() => import('@pages/PatientDashboard'));
const DoctorDashboard = lazy(() => import('@pages/DoctorDashboard'));
const PathologistDashboard = lazy(() => import('@pages/PathologistDashboard'));
const LabTechnicianDashboard = lazy(() => import('@pages/LabTechnicianDashboard'));
const ReceptionistDashboard = lazy(() => import('@pages/ReceptionistDashboard'));
const QueueDisplay = lazy(() => import('@pages/QueueDisplay'));
const PublicBooking = lazy(() => import('@pages/Public/PublicBooking'));
const PharmacyPage = lazy(() => import('@pages/Pharmacy/PharmacyPage'));
const PatientPortal = lazy(() => import('@pages/Public/PatientPortal'));
const ProblemReports = lazy(() => import('@pages/SuperAdmin/ProblemReports'));
const BillingPage = lazy(() => import('@pages/Billing/BillingPage'));
const ReceiptPage = lazy(() => import('@pages/Billing/ReceiptPage'));
const AppointmentScheduling = lazy(() => import('@pages/AppointmentScheduling'));
const PrescriptionList = lazy(() => import('@pages/PrescriptionList'));
const PrescriptionCreate = lazy(() => import('@pages/PrescriptionCreate'));
const PrescriptionView = lazy(() => import('@pages/PrescriptionView'));
const PatientProfile = lazy(() => import('@pages/PatientProfile'));
const AbhaRegistration = lazy(() => import('@pages/AbhaRegistration'));
const LabReportsList = lazy(() => import('@pages/LabReports/LabReportsList'));
const LabReportCreate = lazy(() => import('@pages/LabReports/LabReportCreate'));
const LabReportView = lazy(() => import('@pages/LabReports/LabReportView'));
const LabReportApprove = lazy(() => import('@pages/LabReports/LabReportApprove'));
const SettingsPage = lazy(() => import('@pages/Settings/SettingsPage'));
const AppointmentsList = lazy(() => import('@pages/Appointments/AppointmentsList'));
const AppointmentCreate = lazy(() => import('@pages/Appointments/AppointmentCreate'));
const AppointmentEdit = lazy(() => import('@pages/Appointments/AppointmentEdit'));
const AppointmentsCalendar = lazy(() => import('@pages/Appointments/AppointmentsCalendar'));
const AdminDashboard = lazy(() => import('@pages/Admin/AdminDashboard'));
const UserManagement = lazy(() => import('@pages/Admin/UserManagement'));
const AuditLogPage = lazy(() => import('@pages/Admin/AuditLogPage'));
const BranchesPage = lazy(() => import('@pages/Admin/BranchesPage'));
const SuperAdminDashboard = lazy(() => import('@pages/SuperAdmin/SuperAdminDashboard'));
const OrganizationManagement = lazy(() => import('@pages/SuperAdmin/OrganizationManagement'));
const SystemUserManagement = lazy(() => import('@pages/SuperAdmin/SystemUserManagement'));
const SystemMetrics = lazy(() => import('@pages/SuperAdmin/SystemMetrics'));
const ConsentManagement = lazy(() => import('@pages/DataSharing/ConsentManagement'));
const ShareData = lazy(() => import('@pages/DataSharing/ShareData'));
const DicomStudiesList = lazy(() => import('@pages/DICOM/DicomStudiesList'));
const DicomUpload = lazy(() => import('@pages/DICOM/DicomUpload'));
const DicomViewer = lazy(() => import('@pages/DICOM/DicomViewer'));
const PatientList = lazy(() => import('@pages/Patients/PatientList'));
const PatientView = lazy(() => import('@pages/Patients/PatientView'));
const MedicineManagement = lazy(() => import('@pages/MedicineManagement/MedicineManagement'));
const VaccinationManagement = lazy(() => import('@pages/Vaccinations/VaccinationManagement'));
const OrganizationSettings = lazy(() => import('@pages/OrganizationSettings'));
const OrganizationApiKeys = lazy(() => import('@pages/OrganizationApiKeys'));
const OrganizationMetrics = lazy(() => import('@pages/OrganizationMetrics'));
const DoctorAvailabilitySettings = lazy(() => import('@pages/DoctorAvailabilitySettings'));

const RouteFallback: React.FC = () => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
    <CircularProgress />
  </Box>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactElement; allowedRoles?: string[] }> = ({
  children,
  allowedRoles,
}) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const userRoleString = (typeof user.role === 'string' ? user.role : String(user.role) || '').toLowerCase();
    const normalizedAllowedRoles = allowedRoles.map(r => r.toLowerCase());

    if (!normalizedAllowedRoles.includes(userRoleString)) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
          <Typography variant="h4">Unauthorized - You don't have access to this page</Typography>
        </Box>
      );
    }
  }

  return children;
};

const AppRoutes: React.FC = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/book/:slug" element={<PublicBooking />} />
      <Route path="/my/:token" element={<PatientPortal />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/" replace /> : <RegisterPage />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/" replace /> : <ForgotPasswordPage />} />
      {/* Full-screen waiting-room board (no sidebar) for a TV at the front desk */}
      <Route path="/queue-display" element={<ProtectedRoute allowedRoles={['receptionist', 'doctor', 'admin']}><QueueDisplay /></ProtectedRoute>} />
      {/* Printable receipt without the app chrome */}
      <Route path="/billing/:id/receipt" element={<ProtectedRoute allowedRoles={['receptionist', 'doctor', 'admin']}><ReceiptPage /></ProtectedRoute>} />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route
          index
          element={
            user?.role === UserRole.SUPERADMIN || (user?.role as string) === 'superadmin' ? (
              <SuperAdminDashboard />
            ) : (user?.role as string) === 'admin' ? (
              <AdminDashboard />
            ) : (user?.role as string) === 'doctor' ? (
              <DoctorDashboard />
            ) : (user?.role as string) === 'receptionist' ? (
              <ReceptionistDashboard />
            ) : (user?.role as string) === 'pathologist' ? (
              <PathologistDashboard />
            ) : user?.role === UserRole.LAB_TECHNICIAN ? (
              <LabTechnicianDashboard />
            ) : user?.role === UserRole.RADIOLOGIST ? (
              <Navigate to="/dicom/studies" replace />
            ) : (
              <PatientDashboard />
            )
          }
        />
        
        {/* Profile Route (All Users) */}
        <Route path="profile" element={<ProtectedRoute><PatientProfile /></ProtectedRoute>} />
        
        {/* Patient Management Routes */}
        <Route path="patients" element={<ProtectedRoute allowedRoles={['doctor', 'admin', 'receptionist']}><PatientList /></ProtectedRoute>} />
        <Route path="patients/:id" element={<ProtectedRoute allowedRoles={['doctor', 'admin', 'receptionist']}><PatientView /></ProtectedRoute>} />
        
        {/* ABHA Registration (Patients Only) */}
        <Route path="abha-register" element={<ProtectedRoute allowedRoles={['patient']}><AbhaRegistration /></ProtectedRoute>} />
        
        {/* Doctor Routes */}
        <Route path="prescriptions" element={<ProtectedRoute allowedRoles={['doctor']}><PrescriptionList /></ProtectedRoute>} />
        <Route path="prescriptions/create" element={<ProtectedRoute allowedRoles={['doctor']}><PrescriptionCreate /></ProtectedRoute>} />
        <Route path="prescriptions/:id" element={<ProtectedRoute allowedRoles={['doctor', 'patient']}><PrescriptionView /></ProtectedRoute>} />
        
        {/* Lab Reports Routes */}
        <Route path="lab-reports" element={<ProtectedRoute allowedRoles={['doctor', 'lab_technician', 'pathologist', 'admin']}><LabReportsList /></ProtectedRoute>} />
        <Route path="lab-reports/create" element={<ProtectedRoute allowedRoles={['doctor', 'lab_technician', 'pathologist', 'admin']}><LabReportCreate /></ProtectedRoute>} />
        <Route path="lab-reports/:id" element={<ProtectedRoute allowedRoles={['doctor', 'lab_technician', 'pathologist', 'admin']}><LabReportView /></ProtectedRoute>} />
        <Route path="lab-reports/:id/approve" element={<ProtectedRoute allowedRoles={['pathologist']}><LabReportApprove /></ProtectedRoute>} />
        
        {/* Appointments Routes */}
        <Route path="appointments" element={<ProtectedRoute allowedRoles={['doctor', 'patient', 'receptionist']}><AppointmentsList /></ProtectedRoute>} />
        <Route path="appointments/calendar" element={<ProtectedRoute allowedRoles={['doctor', 'patient', 'receptionist']}><AppointmentsCalendar /></ProtectedRoute>} />
        <Route path="appointments/create" element={<ProtectedRoute allowedRoles={['doctor', 'patient', 'receptionist']}><AppointmentCreate /></ProtectedRoute>} />
        <Route path="appointments/:id/edit" element={<ProtectedRoute allowedRoles={['doctor', 'receptionist']}><AppointmentEdit /></ProtectedRoute>} />
        
        {/* Receptionist Routes */}
        <Route path="receptionist/dashboard" element={<ProtectedRoute allowedRoles={['receptionist']}><ReceptionistDashboard /></ProtectedRoute>} />
        <Route path="receptionist/scheduling" element={<ProtectedRoute allowedRoles={['receptionist']}><AppointmentScheduling /></ProtectedRoute>} />
        <Route path="receptionist/patients" element={<ProtectedRoute allowedRoles={['receptionist', 'doctor', 'admin']}><PatientList /></ProtectedRoute>} />
        
        {/* Admin Routes */}
        <Route path="admin/users" element={<ProtectedRoute allowedRoles={['admin']}><UserManagement /></ProtectedRoute>} />
        <Route path="admin/members" element={<Navigate to="/admin/users" replace />} />
        <Route path="admin/settings" element={<ProtectedRoute allowedRoles={['admin']}><OrganizationSettings /></ProtectedRoute>} />
        <Route path="admin/api-keys" element={<ProtectedRoute allowedRoles={['admin']}><OrganizationApiKeys /></ProtectedRoute>} />
        <Route path="admin/metrics" element={<ProtectedRoute allowedRoles={['admin']}><OrganizationMetrics /></ProtectedRoute>} />
        <Route path="admin/audit-logs" element={<ProtectedRoute allowedRoles={['admin']}><AuditLogPage /></ProtectedRoute>} />
        <Route path="admin/branches" element={<ProtectedRoute allowedRoles={['admin']}><BranchesPage /></ProtectedRoute>} />
        
        {/* SuperAdmin Routes */}
        <Route path="superadmin/dashboard" element={<ProtectedRoute allowedRoles={['superadmin']}><SuperAdminDashboard /></ProtectedRoute>} />
        <Route path="superadmin/organizations" element={<ProtectedRoute allowedRoles={['superadmin']}><OrganizationManagement /></ProtectedRoute>} />
        <Route path="superadmin/reports" element={<ProtectedRoute allowedRoles={['superadmin']}><ProblemReports /></ProtectedRoute>} />
        <Route path="superadmin/users" element={<ProtectedRoute allowedRoles={['superadmin']}><SystemUserManagement /></ProtectedRoute>} />
        <Route path="superadmin/metrics" element={<ProtectedRoute allowedRoles={['superadmin']}><SystemMetrics /></ProtectedRoute>} />
        
        {/* Medicine Management - SuperAdmin, Admin, Doctor */}
        <Route path="medicines" element={<ProtectedRoute allowedRoles={['superadmin', 'admin', 'doctor']}><MedicineManagement /></ProtectedRoute>} />
        
        {/* Vaccination Management - Receptionist, Admin, Doctor */}
        <Route path="vaccinations" element={<ProtectedRoute allowedRoles={['receptionist', 'admin', 'doctor']}><VaccinationManagement /></ProtectedRoute>} />
        
        {/* Doctor Availability Settings - Doctor only */}
        <Route path="billing" element={<ProtectedRoute allowedRoles={['receptionist', 'doctor', 'admin']}><BillingPage /></ProtectedRoute>} />
        <Route path="pharmacy" element={<ProtectedRoute allowedRoles={['receptionist', 'admin']}><PharmacyPage /></ProtectedRoute>} />
        <Route path="doctor/availability" element={<ProtectedRoute allowedRoles={['doctor']}><DoctorAvailabilitySettings /></ProtectedRoute>} />
        
        {/* Data Sharing Routes */}
        <Route path="data-sharing/consents" element={<ProtectedRoute allowedRoles={['patient']}><ConsentManagement /></ProtectedRoute>} />
        <Route path="data-sharing/grant" element={<ProtectedRoute allowedRoles={['patient']}><ShareData /></ProtectedRoute>} />
        
        {/* DICOM/Radiology Routes */}
        <Route path="dicom/studies" element={<ProtectedRoute allowedRoles={['doctor', 'radiologist']}><DicomStudiesList /></ProtectedRoute>} />
        <Route path="dicom/upload" element={<ProtectedRoute allowedRoles={['doctor', 'radiologist']}><DicomUpload /></ProtectedRoute>} />
        <Route path="dicom/viewer/:studyId" element={<ProtectedRoute allowedRoles={['doctor', 'radiologist']}><DicomViewer /></ProtectedRoute>} />
        
        {/* Settings Route (All Users) */}
        <Route path="settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

/** Applies the chosen light/dark theme. */
const ThemedApp: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { mode } = usePreferences();
  const theme = React.useMemo(() => makeTheme(mode), [mode]);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

const App: React.FC = () => {
  return (
    <PreferencesProvider>
    <ThemedApp>
      <CssBaseline />
      <ErrorBoundary>
        <SnackbarProvider maxSnack={3} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
          <ApiNotices />
          <BrowserRouter>
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <NotificationProvider>
                  <ErrorBoundary>
                    <Suspense fallback={<RouteFallback />}>
                      <AppRoutes />
                    </Suspense>
                  </ErrorBoundary>
                </NotificationProvider>
              </AuthProvider>
            </QueryClientProvider>
          </BrowserRouter>
        </SnackbarProvider>
      </ErrorBoundary>
    </ThemedApp>
    </PreferencesProvider>
  );
};

export default App;
